/**
 * ControlAttestationService — V96-4-9 (extends D-96-9 structured attestation).
 *
 * A person may attest only to controls the security baseline marks
 * `attested` (policies, governance, HR-style obligations). Machine-evidenced
 * controls must be evidenced by a connected source — an attestation can
 * never stand in for a machine check. The attestation is recorded as a
 * passing check under the workspace's attestation connector, naming who
 * attested and what they attested to, so it ages out under the normal
 * freshness rules like any other evidence.
 */
import { SECURITY_BASELINE_CONTROLS } from '../data/security-baseline.js';
import { getOrCreateAttestationConnector } from './recurring-obligation-service.js';
import { recordCheck } from './control-check-service.js';
const ATTESTABLE = new Map(SECURITY_BASELINE_CONTROLS.filter((c) => c.mode === 'attested').map((c) => [c.id, c]));
export class ControlAttestationError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'ControlAttestationError';
    }
}
export function isAttestableControl(controlId) {
    return ATTESTABLE.has(controlId);
}
export async function attestControl(db, storage, input) {
    const control = ATTESTABLE.get(input.control_id);
    if (!control) {
        throw new ControlAttestationError('not_attestable', `${input.control_id} is evidenced by a connected source, not by attestation.`);
    }
    const summary = input.summary.trim();
    if (summary.length < 20)
        throw new ControlAttestationError('invalid', 'Describe what was done (at least 20 characters).');
    if (input.evidence_sha256 && !/^[a-f0-9]{64}$/.test(input.evidence_sha256)) {
        throw new ControlAttestationError('invalid', 'evidence_sha256 must be a SHA-256 hex digest.');
    }
    const connectorId = await getOrCreateAttestationConnector(db, { workspace_id: input.workspace_id, user_id: input.user_id, newId: input.newId });
    const details = {
        attestation: 'control', control_title: control.title, summary,
        evidence_sha256: input.evidence_sha256 ?? null, attested_by: input.user_id,
    };
    const bytes = new TextEncoder().encode(JSON.stringify(details));
    const check = await recordCheck(db, storage, {
        check_id: input.newId(), workspace_id: input.workspace_id, connector_id: connectorId, control_id: input.control_id,
        passed: true, evidence_bytes: bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength),
        collector_version: 'control-attestation/1', details, connector_kind: 'local_upload', check_name: `attestation:${input.control_id}`,
    });
    return { check_id: check.check_id, control_id: input.control_id };
}
//# sourceMappingURL=control-attestation-service.js.map